import { Menu, MenuButton, MenuDivider, MenuItem, MenuList, Tooltip } from "@chakra-ui/react";
import { Fragment } from "react";
import styled from "styled-components";
import { useActorActionsMenu } from "src/apps/hooks/useActorActionsMenu";
import { useTranslations } from "src/apps/hooks/useTranslations";
import type { ActorActionName } from "src/contexts/documentationArea/domain/ActorAction";
import { MobileModal } from "src/apps/components/modal/MobileModal";
import { ActorInvitationModal } from "./ActorInvitationModal";
import { ActorUserAccessModal } from "./ActorUserAccessModal";
import { ActorUserRevokeAccessModal } from "./ActorUserRevokeAccessModal";

import { colors, Icon as PCIcon, type IconName } from "@permisso/permisso-components";

const MORE_ACTIONS_TEXT = "documentation_area.access_actions.button";

const ACTION_ICON_NAMES: Record<ActorActionName, IconName> = {
  invite_user: "file-user",
  invite_actor: "users",
  delete_user_access: "file-x-corner",
};

type ActorActionsMenuProps = {
  isMobile: boolean;
};

export const ActorActionsMenu = ({ isMobile }: ActorActionsMenuProps) => {
  const { t } = useTranslations();
  const {
    actions,
    revokeOption,
    revocableAccesses,
    isMobileMenuOpen,
    activeAction,
    openMobileMenu,
    closeMobileMenu,
    handleOptionAction,
    closeActiveAction,
  } = useActorActionsMenu();

  const renderOptions = (mobile: boolean) =>
    actions.map((option, index) => {
      const previousOption = actions[index - 1];
      const content = (
        <MenuActionItem>
          <MenuActionIcon>
            <PCIcon name={ACTION_ICON_NAMES[option.action]} size="s20" />
          </MenuActionIcon>
          <MenuActionLabel>{t(option.key)}</MenuActionLabel>
        </MenuActionItem>
      );
      const item = mobile ? (
        <MobileMenuItem
          type="button"
          disabled={option.disabled}
          title={option.disabledReason ? t(option.disabledReason) : undefined}
          onClick={() => handleOptionAction(option.action)}
        >
          {content}
        </MobileMenuItem>
      ) : (
        <MenuItem isDisabled={option.disabled} onClick={() => handleOptionAction(option.action)}>
          {content}
        </MenuItem>
      );
      const itemWithReason =
        !mobile && option.disabledReason ? (
          <Tooltip hasArrow label={t(option.disabledReason)}>
            <MenuItemTooltipWrapper>{item}</MenuItemTooltipWrapper>
          </Tooltip>
        ) : (
          item
        );

      return (
        <Fragment key={`${option.section}-${option.action}`}>
          {previousOption && previousOption.section !== option.section ? (
            mobile ? (
              <MobileMenuDivider />
            ) : (
              <StyledMenuDivider />
            )
          ) : null}
          {itemWithReason}
        </Fragment>
      );
    });

  if (actions.length === 0) return null;

  return (
    <>
      {isMobile ? (
        <ActionsButton
          type="button"
          aria-label={t(MORE_ACTIONS_TEXT)}
          $iconOnly
          onClick={openMobileMenu}
        >
          <ActionsButtonContent>
            <PCIcon name="plus" size="s20" color="grey800" />
          </ActionsButtonContent>
        </ActionsButton>
      ) : (
        <Menu placement="bottom-end">
          <MenuButton as={ActionsButton} aria-label={t(MORE_ACTIONS_TEXT)}>
            <ActionsButtonContent>
              <PCIcon name="plus" size="s20" color="grey800" />
              {t(MORE_ACTIONS_TEXT)}
            </ActionsButtonContent>
          </MenuButton>
          <ActionsMenuList>{renderOptions(false)}</ActionsMenuList>
        </Menu>
      )}
      <MobileModal isOpen={isMobile && isMobileMenuOpen} onClose={closeMobileMenu}>
        <MobileActionsList>{renderOptions(true)}</MobileActionsList>
      </MobileModal>
      <ActorInvitationModal isOpen={activeAction === "invite_actor"} onClose={closeActiveAction} />
      <ActorUserAccessModal isOpen={activeAction === "invite_user"} onClose={closeActiveAction} />
      {revokeOption ? (
        <ActorUserRevokeAccessModal
          isOpen={activeAction === "delete_user_access"}
          onClose={closeActiveAction}
          accesses={revocableAccesses}
        />
      ) : null}
    </>
  );
};

const ActionsButton = styled.button<{ $iconOnly?: boolean }>`
  display: flex;
  margin-left: auto;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: ${({ $iconOnly }) => ($iconOnly ? "2.5rem" : "auto")};
  min-height: 2.5rem;
  padding: 0.5rem;
  background-color: ${colors.grey0};
  border: 1px solid ${colors.grey600};
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0;
  color: ${colors.grey800};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.grey200};
  }
`;

const ActionsButtonContent = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const ActionsMenuList = styled(MenuList)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: max-content;
  min-width: 17.125rem;
  max-width: calc(100vw - 2rem);
  padding: 8px;
  background-color: ${colors.grey0};
  border: 1px solid ${colors.grey500};
  border-radius: 8px;
  box-shadow: 0 0 2px rgba(26, 26, 26, 0.15);

  [role="menuitem"] {
    display: flex;
    width: 100%;
    min-width: 0;
    height: 2rem;
    padding: 0 8px;
    gap: 8px;
    border-radius: 4px;
    color: ${colors.grey700};
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    letter-spacing: 0;
  }
`;

const MenuItemTooltipWrapper = styled.span`
  display: flex;
  width: 100%;
`;

const MobileActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 16px;
`;

const MobileMenuItem = styled.button`
  display: flex;
  width: 100%;
  min-width: 0;
  height: 2rem;
  padding: 0 8px;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 4px;
  background: ${colors.grey0};
  color: ${colors.grey700};
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const MobileMenuDivider = styled.div`
  width: 100%;
  border-top: 1px solid ${colors.grey500};
`;

const MenuActionItem = styled.span`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 10px;
`;

const MenuActionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuActionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledMenuDivider = styled(MenuDivider)`
  flex: 0 0 1px;
  width: 100%;
  margin: 0 !important;
  border: 0;
  border-top: 1px solid ${colors.grey500};
`;
